import { Link } from 'react-router-dom';
import SharedTrustBar from '../../components/feature/SharedTrustBar';
import SharedAIAnswerBlock from '../../components/feature/SharedAIAnswerBlock';
import SharedProcess from '../../components/feature/SharedProcess';
import SharedWhyChooseUs from '../../components/feature/SharedWhyChooseUs';
import SharedServiceAreaSection from '../../components/feature/SharedServiceAreaSection';
import SharedTestimonials from '../../components/feature/SharedTestimonials';
import SharedFAQ from '../../components/feature/SharedFAQ';
import SharedFinalCTA from '../../components/feature/SharedFinalCTA';

const SERVICE_BREAKDOWN = [
  {
    icon: 'ri-sun-line',
    title: 'Daily Office Cleaning',
    desc: 'Morning or evening cleaning scheduled around your business hours. Floors vacuumed, surfaces wiped, common areas refreshed before your team arrives each day.',
    img: 'https://public.readdy.ai/ai/img_res/4b16235545435e78b38ed475e998d309.jpg',
  },
  {
    icon: 'ri-spray-line',
    title: 'Desk & Surface Disinfection',
    desc: 'High-touch surfaces disinfected using EPA-approved commercial products. Keyboards, phones, desks, and shared surfaces cleaned and sanitized on every visit.',
    img: 'https://readdy.ai/api/search-image?query=professional%20cleaner%20disinfecting%20office%20desk%20surfaces%20keyboard%20phone%20spray%20bottle%20microfiber%20cloth%20commercial%20office%20cleaning%20service%20bright%20workspace%20health%20safety&width=600&height=380&seq=ofcsvc002&orientation=landscape',
  },
  {
    icon: 'ri-delete-bin-line',
    title: 'Trash & Recycling Removal',
    desc: 'All waste stations emptied and lined. Recyclables sorted and disposed of properly. Breakroom and restroom bins handled on every visit without exception.',
    img: 'https://readdy.ai/api/search-image?query=clean%20office%20waste%20management%20recycling%20trash%20removal%20commercial%20building%20service%20bright%20corporate%20environment%20professional%20service%20organized%20clean%20office&width=600&height=380&seq=ofcsvc003&orientation=landscape',
  },
  {
    icon: 'ri-cup-line',
    title: 'Breakroom & Kitchen Cleaning',
    desc: 'All breakroom surfaces cleaned, appliances wiped, sinks scrubbed, and countertops sanitized — keeping shared spaces fresh, hygienic, and ready for use.',
    img: 'https://readdy.ai/api/search-image?query=clean%20modern%20office%20breakroom%20kitchen%20area%20spotless%20countertops%20stainless%20appliances%20bright%20contemporary%20design%20professional%20commercial%20cleaning%20result%20sparkling%20clean&width=600&height=380&seq=ofcsvc004&orientation=landscape',
  },
  {
    icon: 'ri-drop-line',
    title: 'Restroom Sanitation',
    desc: 'Full restroom disinfection including toilets, sinks, mirrors, floors, and supply restocking. Hospital-grade cleaning protocols on every scheduled visit.',
    img: 'https://readdy.ai/api/search-image?query=pristine%20sparkling%20clean%20modern%20office%20restroom%20bathroom%20white%20tiles%20bright%20lights%20polished%20fixtures%20professional%20commercial%20sanitation%20result%20immaculate%20hygiene&width=600&height=380&seq=ofcsvc005&orientation=landscape',
  },
];

const AI_ITEMS = [
  {
    question: 'What is included in office cleaning services?',
    answer: 'Office cleaning includes dusting, vacuuming, disinfecting surfaces, restroom cleaning, trash removal, and maintaining shared spaces. OJS Janitorial customizes services based on your office layout and cleaning needs, ensuring a clean, professional workplace every day.',
  },
  {
    question: 'How often should an office be professionally cleaned?',
    answer: 'Most offices require cleaning 3–5 times per week depending on employee count and foot traffic. High-traffic offices with 50+ employees often need daily service. OJS Janitorial will recommend the right schedule after a free on-site assessment of your workspace.',
  },
  {
    question: 'What are the benefits of professional office cleaning?',
    answer: 'Professional office cleaning improves employee productivity, reduces sick days by eliminating germs, creates better first impressions for clients, and ensures consistent cleanliness across all areas — without the cost and hassle of managing in-house cleaning staff.',
  },
  {
    question: 'Does OJS Janitorial offer after-hours office cleaning?',
    answer: 'Yes, OJS Janitorial offers fully flexible scheduling including before-business-hours, after-hours, and weekend office cleaning. This ensures cleaning never disrupts daily operations while keeping the workspace consistently clean and professional.',
  },
];

const WHY_BULLETS = [
  { icon: 'ri-user-star-line', title: 'Office-Trained Staff', desc: 'Crew members are specifically trained for professional office environments — discreet, thorough, and respectful of your workspace.' },
  { icon: 'ri-leaf-line', title: 'Eco-Friendly Products', desc: 'We use EPA-approved, eco-conscious cleaning products that are safe for people, pets, and the office environment.' },
  { icon: 'ri-id-card-line', title: 'Background-Checked Crew', desc: 'All cleaning staff are thoroughly background-checked and uniformed — ensuring your office is always in trusted hands.' },
  { icon: 'ri-time-line', title: 'Your Schedule, Our Hours', desc: 'Before-hours, after-hours, weekends — we clean around your business, not the other way around.' },
  { icon: 'ri-customer-service-2-line', title: 'Dedicated Account Manager', desc: 'Every client gets a dedicated point of contact for scheduling, feedback, and any adjustments to your cleaning plan.' },
];

const FAQS = [
  {
    q: 'How often should an office be cleaned?',
    a: 'Most offices benefit from cleaning 3–5 times per week depending on employee count and daily foot traffic. High-traffic offices with open floor plans may need daily service. OJS Janitorial recommends a schedule after a free on-site walkthrough of your specific workspace and needs.',
  },
  {
    q: 'What is included in office cleaning services near me?',
    a: 'Office cleaning services include vacuuming, dusting, surface disinfection, restroom sanitation, trash removal, and breakroom cleaning. OJS Janitorial in Metairie, LA customizes every cleaning plan to match your office layout, size, and specific cleaning requirements.',
  },
  {
    q: 'How much does office cleaning cost in Metairie, LA?',
    a: 'Office cleaning costs in Metairie vary based on office size, number of rooms, cleaning frequency, and specific services required. OJS Janitorial provides free, no-obligation custom quotes. Call (504) 818-3347 or fill out the online form to get your personalized estimate today.',
  },
  {
    q: 'Do you offer after-hours office cleaning services?',
    a: 'Yes, OJS Janitorial offers flexible scheduling including early morning, evening, and weekend office cleaning. This ensures your cleaning is completely invisible to employees and clients, maintaining a spotless workspace without interrupting normal business operations throughout the day.',
  },
  {
    q: 'How long does office cleaning take?',
    a: 'Office cleaning time depends on office size and the scope of services. A small 1,000 sq ft office typically takes 1–2 hours. Larger facilities may take 3–5 hours. OJS Janitorial provides an accurate time estimate during the free on-site assessment process.',
  },
];

export default function OfficeCleaningPage() {
  return (
    <main>
      {/* Hero */}
      <section
        className="relative pt-32 pb-24 overflow-hidden"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=bright%20clean%20modern%20professional%20office%20space%20open%20plan%20white%20interior%20fresh%20air%20natural%20light%20large%20windows%20plants%20minimalist%20desk%20arrangement%20immaculate%20pristine%20corporate%20workspace%20polished%20wood%20floors%20contemporary%20design&width=1920&height=900&seq=ofc_hero_v2&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/85 to-navy/50" />
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'linear-gradient(rgba(0,230,122,1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,230,122,1) 1px, transparent 1px)', backgroundSize: '80px 80px' }} />
        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 bg-accent/15 border border-accent/30 rounded-full px-4 py-1.5 mb-6">
            <i className="ri-building-2-line text-accent text-sm"></i>
            <span className="text-accent text-xs font-body font-semibold uppercase tracking-widest">Office Cleaning Services</span>
          </div>
          <h1 className="font-heading font-extrabold text-white text-5xl xl:text-6xl mb-6 leading-tight max-w-3xl">
            Office Cleaning Services for <span className="text-accent">Professional Workspaces</span>
          </h1>
          <p className="text-white/65 font-body text-xl max-w-2xl mb-10 leading-relaxed">
            Tailored commercial office cleaning in Metairie &amp; New Orleans, LA. We handle every detail so your team can focus on what matters most.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/contact" className="bg-accent hover:bg-accent-dark text-navy font-heading font-extrabold px-9 py-4 rounded-full transition-all whitespace-nowrap flex items-center gap-2">
              Get a Free Quote <i className="ri-arrow-right-line"></i>
            </Link>
            <a href="tel:+15048183347" className="bg-white/10 hover:bg-white/20 border border-white/20 text-white font-heading font-bold px-9 py-4 rounded-full transition-all whitespace-nowrap flex items-center gap-2 backdrop-blur-sm">
              <i className="ri-phone-fill text-accent"></i> (504) 818-3347
            </a>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <SharedTrustBar />

      {/* Service Breakdown */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-accent/10 border border-accent/20 rounded-full px-4 py-1.5 mb-5">
              <span className="text-accent text-xs font-body font-semibold uppercase tracking-wider">Service Breakdown</span>
            </div>
            <h2 className="font-heading font-extrabold text-navy text-4xl xl:text-5xl mb-4">
              What&apos;s Included in Office Cleaning
            </h2>
            <p className="text-gray-500 font-body text-lg max-w-xl mx-auto">Every visit covers the essentials — and more when your office needs it.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICE_BREAKDOWN.map((item) => (
              <div key={item.title} className="bg-gray-50 rounded-2xl overflow-hidden border border-gray-100 hover:border-accent/25 hover:-translate-y-0.5 transition-all duration-300 group">
                <div className="w-full h-44 overflow-hidden bg-gray-200">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-accent/10 rounded-lg shrink-0">
                      <i className={`${item.icon} text-accent text-base`}></i>
                    </div>
                    <h3 className="font-heading font-bold text-navy text-base">{item.title}</h3>
                  </div>
                  <p className="text-gray-500 font-body text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Answer Block */}
      <SharedAIAnswerBlock label="AI Answer Block — Office Cleaning" items={AI_ITEMS} />

      {/* Process */}
      <SharedProcess
        title="Our Office Cleaning Process"
        subtitle="From your first call to ongoing service — a seamless process built around your office schedule and needs."
      />

      {/* Why Choose Us */}
      <SharedWhyChooseUs
        title="Why Choose OJS for Your Office Cleaning?"
        subtitle="Office-trained staff, eco-friendly products, and flexible scheduling — professional office cleaning done right every single time."
        bullets={WHY_BULLETS}
        imageSrc="https://readdy.ai/api/search-image?query=professional%20commercial%20office%20cleaning%20team%20two%20workers%20uniform%20cleaning%20corporate%20office%20space%20professional%20equipment%20bright%20modern%20interior%20trustworthy%20service%20team%20smiling%20professional%20appearance&width=800&height=900&seq=ofc_why001&orientation=portrait"
        imageAlt="Professional OJS office cleaning team"
        ctaText="Get an Office Cleaning Quote"
      />

      {/* Service Areas */}
      <SharedServiceAreaSection
        title="Office Cleaning Across Greater New Orleans"
        subtitle="Serving offices and professional workspaces throughout Metairie, New Orleans, Kenner, Harahan, Jefferson Parish, and surrounding areas."
      />

      {/* Testimonials */}
      <SharedTestimonials
        title="What Office Managers Say About OJS"
        subtitle="Trusted by businesses across Metairie and New Orleans for consistent, professional office cleaning service."
      />

      {/* FAQ */}
      <SharedFAQ title="Office Cleaning FAQs — Voice Search Optimized" faqs={FAQS} />

      {/* Final CTA */}
      <SharedFinalCTA
        title="Ready for a Spotless Office?"
        subtitle="Get a free, customized office cleaning quote for your Metairie or New Orleans workspace. Response guaranteed within 24 hours."
        ctaButtonText="Request Office Cleaning Quote"
        serviceLabel="Office Cleaning"
      />
    </main>
  );
}
